import { ReactElement, useCallback } from 'react';
import { useFormik, FormikProvider, FormikHelpers } from 'formik';
import { Typography, Stack, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { hasError, getHelperText } from '~utils/formHelpers';
import { getItemValue } from '~utils/helpers';
import { getDefaultAvatar } from '~utils/user';
import { FileUpload, PasswordVisibilityAdornment } from '~components/index';
import { useLazyRequest, useToggle } from '~hooks/index';
import { uploadImages } from '~services/files/index';
import { FileResponse } from '~services/files/types';
import { createDriver } from '~services/admin/driver';
import { Driver, DriverCreateBody } from '~services/admin/driver/types';
import { STATUS_CODES, PATHS } from '~constants/index';
import { DataResultsResponse, Nullable } from '~globals/types';

import { FIELDS_NAME, initialValues, validationSchema } from './utils';
import { ConfigUserFormAvatar, ConfigUserFormSubmitButton } from './styles';

const ConfigDriverForm = (): ReactElement => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [, loadingCreateDriver, , executeCreateDriver] = useLazyRequest({
    request: createDriver,
    withPostSuccess: (response) => {
      const userResponse = response.data?.data as Driver;
      const userName = `${userResponse.lastName}, ${userResponse.firstName}`;
      const message = `El chofer ${userName} fue creado correctamente`;

      enqueueSnackbar(message, { variant: 'success' });

      navigate(PATHS.CONFIG.DRIVERS.BASE);
    },
    withPostFailure: (error) => {
      let errorMessage = 'Ha ocurrido un error, intente nuevamente';

      if (error.data?.data.errorCode === STATUS_CODES.userExist) {
        errorMessage =
          // eslint-disable-next-line max-len
          'El email que ingresado ya se encuentra registrado. Por favor, modifíquelo e intenté nuevamente.';
      }

      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });

  const onSubmit = useCallback(
    async (
      values: typeof initialValues,
      { setSubmitting }: FormikHelpers<typeof initialValues>,
    ) => {
      try {
        const avatarValue = values[FIELDS_NAME.AVATAR];
        let avatarFileId: Nullable<string> = null;

        if (avatarValue.length > 0) {
          const dataUploadFile = await uploadImages(avatarValue);
          const {
            data: { results },
          } = dataUploadFile.data as DataResultsResponse<FileResponse[]>;

          avatarFileId = results[0].fileId;
        }

        const body: DriverCreateBody = {
          avatarFileId,
          email: values[FIELDS_NAME.EMAIL],
          password: values[FIELDS_NAME.PASSWORD],
          firstName: values[FIELDS_NAME.FIRST_NAME],
          lastName: values[FIELDS_NAME.LAST_NAME],
          idNumber: getItemValue(values, FIELDS_NAME.DOCUMENT_NUMBER),
          code: getItemValue(values, FIELDS_NAME.REFERENCE),
          contactPhone: getItemValue(values, FIELDS_NAME.CONTACT_PHONE),
          contactMobile: getItemValue(values, FIELDS_NAME.CONTACT_MOBILE),
          roleId: 2,
          enabled: true,
          isOwner: false,
        };

        await executeCreateDriver(body);
      } catch (_err) {
        enqueueSnackbar('Ha ocurrido un error, intente nuevamente', {
          variant: 'error',
        });
      } finally {
        setSubmitting(false);
      }
    },
    [enqueueSnackbar, executeCreateDriver],
  );

  const formikBag = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const { errors, touched, getFieldProps, handleSubmit, values, isSubmitting } =
    formikBag;

  const [showPassword, togglePassword] = useToggle();
  const [showConfirmPassword, toggleConfirmPassword] = useToggle();

  return (
    <FormikProvider value={formikBag}>
      <Stack spacing={2} component="form" onSubmit={handleSubmit}>
        <Stack spacing={2} direction="row">
          <Stack spacing={2} flex={1}>
            <Typography component="h6" fontWeight="bold">
              Información de contacto
            </Typography>

            <Stack spacing={2} direction="row" alignItems="center">
              <FileUpload
                name={FIELDS_NAME.AVATAR}
                label="Subir avatar"
                enabledChipsInfo={false}
              />

              <ConfigUserFormAvatar
                src={getDefaultAvatar(
                  values.avatar.length > 0
                    ? URL.createObjectURL(values.avatar[0].file)
                    : undefined,
                )}
              />
            </Stack>

            <TextField
              label="Email"
              autoComplete="off"
              error={hasError(touched, errors, FIELDS_NAME.EMAIL)}
              helperText={getHelperText(touched, errors, FIELDS_NAME.EMAIL)}
              {...getFieldProps(FIELDS_NAME.EMAIL)}
            />

            <TextField
              label="Contraseña"
              autoComplete="off"
              error={hasError(touched, errors, FIELDS_NAME.PASSWORD)}
              helperText={getHelperText(touched, errors, FIELDS_NAME.PASSWORD)}
              {...getFieldProps(FIELDS_NAME.PASSWORD)}
              type={showPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <PasswordVisibilityAdornment
                    showPassword={showPassword}
                    onClick={() => togglePassword()}
                    position="end"
                  />
                ),
              }}
            />

            <TextField
              fullWidth
              autoComplete="off"
              label="Confirmar Contraseña"
              error={hasError(touched, errors, FIELDS_NAME.CONFIRM_PASSWORD)}
              helperText={getHelperText(
                touched,
                errors,
                FIELDS_NAME.CONFIRM_PASSWORD,
              )}
              {...getFieldProps(FIELDS_NAME.CONFIRM_PASSWORD)}
              type={showConfirmPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <PasswordVisibilityAdornment
                    showPassword={showConfirmPassword}
                    onClick={() => toggleConfirmPassword()}
                    position="end"
                  />
                ),
              }}
            />

            <TextField
              label="Nombre"
              autoComplete="off"
              error={hasError(touched, errors, FIELDS_NAME.FIRST_NAME)}
              helperText={getHelperText(
                touched,
                errors,
                FIELDS_NAME.FIRST_NAME,
              )}
              {...getFieldProps(FIELDS_NAME.FIRST_NAME)}
            />

            <TextField
              label="Apellido"
              autoComplete="off"
              error={hasError(touched, errors, FIELDS_NAME.LAST_NAME)}
              helperText={getHelperText(touched, errors, FIELDS_NAME.LAST_NAME)}
              {...getFieldProps(FIELDS_NAME.LAST_NAME)}
            />

            <TextField
              label="Número de documento (Opcional)"
              autoComplete="off"
              error={hasError(touched, errors, FIELDS_NAME.DOCUMENT_NUMBER)}
              helperText={getHelperText(
                touched,
                errors,
                FIELDS_NAME.DOCUMENT_NUMBER,
              )}
              {...getFieldProps(FIELDS_NAME.DOCUMENT_NUMBER)}
            />

            <TextField
              label="Referencia (Opcional)"
              autoComplete="off"
              error={hasError(touched, errors, FIELDS_NAME.REFERENCE)}
              helperText={getHelperText(touched, errors, FIELDS_NAME.REFERENCE)}
              {...getFieldProps(FIELDS_NAME.REFERENCE)}
            />

            <TextField
              label="Teléfono (Opcional)"
              placeholder="Cod de área sin 0 + Nro sin 15"
              autoComplete="off"
              error={hasError(touched, errors, FIELDS_NAME.CONTACT_PHONE)}
              helperText={getHelperText(
                touched,
                errors,
                FIELDS_NAME.CONTACT_PHONE,
                'Cod de área sin 0 + Nro sin 15',
              )}
              {...getFieldProps(FIELDS_NAME.CONTACT_PHONE)}
            />

            <TextField
              label="Celular / Whatsapp (Opcional)"
              placeholder="Cod de área sin 0 + Nro sin 15"
              autoComplete="off"
              error={hasError(touched, errors, FIELDS_NAME.CONTACT_MOBILE)}
              helperText={getHelperText(
                touched,
                errors,
                FIELDS_NAME.CONTACT_MOBILE,
                'Cod de área sin 0 + Nro sin 15',
              )}
              {...getFieldProps(FIELDS_NAME.CONTACT_MOBILE)}
            />
          </Stack>

          <Stack spacing={2} flex={1}></Stack>
        </Stack>

        <Stack direction="row" spacing={2}>
          <ConfigUserFormSubmitButton
            type="submit"
            variant="contained"
            color="primary"
            loading={loadingCreateDriver || isSubmitting}
          >
            Crear
          </ConfigUserFormSubmitButton>
        </Stack>
      </Stack>
    </FormikProvider>
  );
};

export default ConfigDriverForm;
